import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import NatalFormActions from '../components/natal/NatalFormActions';
import NatalChartStyles from '../components/natal/NatalChartStyles';
import NatalBirthForm from '../components/natal/NatalBirthForm';
import NatalChartHeader from '../components/natal/NatalChartHeader';
import NatalChartResult from '../components/natal/NatalChartResult';
import useNatalGeo, {
  geoRuntimeRowMetadata,
  NATAL_GEO_RUNTIME_VERSION,
} from '../hooks/useNatalGeo';
import useNatalChart from '../hooks/useNatalChart';
import {
  aspectTypeFromItem,
  aspectTitleFromItem,
  aspectMetaParts,
  aspectTextFromItem,
  tAspect,
  aspectSymbol,
  ensureArray,
  splitAspectNarrative,
  buildEditorialAspectSections,
  aspectIntensityTier,
  prioritizeAspects,
  aspectToneMeta,
  aspectBadgeStyle,
  renderBulletChipText,
  isStructuredInterpretationLine,
  buildPatternRowsFromBullets,
  safeIntegratedTitle,
  safeIntegratedSubtitle,
  stripMd,
  splitLines,
  lineKind,
  kindMeta,
  stripPrefix,
  normalizePlanetHeaderLine,
  isDecorativeOnlyLine,
  decoratePlanetCardTitle,
  buildCompactPlanetEditorial,
  compactPlacementDegree,
  buildExpandedPlanetEditorial,
} from '../components/natal/natalInterpretationHelpers';
import {
  SHARED_CHIP_STYLE,
  GEO,
  tSign,
  tPlanet,
  formatDegree,
  getDisplayDegree,
  formatRetro,
} from '../components/natal/natalConstants';

const containerStyle = {
  minHeight: '100svh',
  width: '100%',
  background: '#070617',
  backgroundColor: '#070617',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'flex-start',
  boxSizing: 'border-box',
  overflowX: 'hidden',
  position: 'relative',
  isolation: 'isolate',
  paddingLeft: '14px',
  paddingRight: '14px',
  paddingTop: 'calc(22px + env(safe-area-inset-top, 0px))',
  paddingBottom: 'calc(24px + env(safe-area-inset-bottom, 0px))',
  color: '#fff',
};

const cardStyle = {
  width: '100%',
  maxWidth: 500,
  padding: '8px 0 18px',
  marginTop: 0,
  background: 'transparent',
  backgroundColor: 'transparent',
  border: 'none',
  boxShadow: 'none',
  overflow: 'visible',
};

export default function NatalChartScreen() {
  const { user } = useAuth();
  const authUserId = (user && (user._id || user.id || user.userId)) || null;

  const [openAcc, setOpenAcc] = useState('');
  const toggleAcc = (id) => {
    setOpenAcc((prev) => (prev === id ? '' : id));
  };

  const [showBirthData, setShowBirthData] = useState(false);

  const [form, setForm] = useState({
    birthDate: '',
    birthTime: '',
    birthCountry: 'AR',
    birthProvince: 'CABA',
    birthCity: 'Capital Federal',
    birthNeighborhood: '',
    birthPlaceManual: '',
    birthPlace: '',
    birthLat: null,
    birthLon: null,
    birthCityQuery: '',
    birthTzOffsetMin: null,
    birthTimezone: null,
  });

  const {
    actionsRef,
    applyGeoPick,
    applyResolvedTypedPlace,
    buildBirthPlaceString,
    cityInputRef,
    dropdownRect,
    findGeoAliasPreset,
    geoLoading,
    geoTimezoneError,
    geoTimezoneResolving,
    invalidateGeoTimezoneResolution,
    geoOpen,
    geoResults,
    refreshDropdownRect,
    resolveCoords,
    resolveTypedPlaceData,
    setGeoOpen,
    setGeoResults,
    tryResolveTypedPlace,
    updateGeo,
  } = useNatalGeo({ form, setForm });

  const {
    chart,
    error,
    interpretations,
    loadingChart,
    loadingSave,
    saveAndFetchChart,
    success,
    summary,
  } = useNatalChart({
    form,
    authUserId,
    resolveTypedPlaceData,
    applyResolvedTypedPlace,
  });

  const handleChange = (event) => {
    const { name, value } = event.target;
    if (name === 'birthDate' || name === 'birthTime') {
      invalidateGeoTimezoneResolution();
    }
    setForm((prev) => ({
      ...prev,
      [name]: value,
      ...((name === 'birthDate' || name === 'birthTime') ? { birthTzOffsetMin: null } : {}),
    }));
  };

  if (process.env.NODE_ENV !== 'production') {
    console.debug('[NATAL_GEO_SCREEN_TRACE]', {
      runtimeVersion: NATAL_GEO_RUNTIME_VERSION,
      count: Array.isArray(geoResults) ? geoResults.length : 0,
      rows: Array.isArray(geoResults)
        ? geoResults.map((row, index) => geoRuntimeRowMetadata(row, index))
        : [],
    });
  }

  return (
    <div id="natalRoot" style={containerStyle}>
      <NatalChartStyles />

      <div className="natal-content-column" style={{ ...cardStyle, zIndex: 1 }}>
        <NatalChartHeader
          chart={chart}
          showBirthData={showBirthData}
          onToggle={() => setShowBirthData((prev) => !prev)}
        />

        <NatalBirthForm
          visible={showBirthData || !chart}
          chart={chart}
          form={form}
          handleChange={handleChange}
          updateGeo={updateGeo}
          geoData={GEO}
          cityInputRef={cityInputRef}
          findGeoAliasPreset={findGeoAliasPreset}
          setForm={setForm}
          buildBirthPlaceString={buildBirthPlaceString}
          setGeoOpen={setGeoOpen}
          setGeoResults={setGeoResults}
          refreshDropdownRect={refreshDropdownRect}
          tryResolveTypedPlace={tryResolveTypedPlace}
          geoOpen={geoOpen}
          geoLoading={geoLoading}
          geoTimezoneError={geoTimezoneError}
          geoTimezoneResolving={geoTimezoneResolving}
          invalidateGeoTimezoneResolution={invalidateGeoTimezoneResolution}
          geoResults={geoResults}
          dropdownRect={dropdownRect}
          applyGeoPick={applyGeoPick}
          resolveCoords={resolveCoords}
        />

        {showBirthData || !chart ? (
          <NatalFormActions
            actionsRef={actionsRef}
            loadingSave={loadingSave}
            loadingChart={loadingChart}
            disabled={geoTimezoneResolving}
            onSave={saveAndFetchChart}
          />
        ) : null}

        {error ? (
          <p className="natal-status-message is-error">
            {error}
          </p>
        ) : null}

        {success && !error ? (
          <p className="natal-status-message is-success">
            {success}
          </p>
        ) : null}

        <NatalChartResult
          chart={chart}
          summary={summary}
          interpretations={interpretations}
          openAcc={openAcc}
          toggleAcc={toggleAcc}
          helpers={{
            tSign,
            tPlanet,
            formatDegree,
            getDisplayDegree,
            formatRetro,
            SHARED_CHIP_STYLE,
            ensureArray,
            renderBulletChipText,
            stripMd,
            buildPatternRowsFromBullets,
            aspectTitleFromItem,
            aspectTextFromItem,
            aspectMetaParts,
            aspectTypeFromItem,
            aspectToneMeta,
            splitAspectNarrative,
            buildEditorialAspectSections,
            aspectIntensityTier,
            prioritizeAspects,
            aspectBadgeStyle,
            aspectSymbol,
            tAspect,
            safeIntegratedTitle,
            safeIntegratedSubtitle,
            decoratePlanetCardTitle,
            splitLines,
            isStructuredInterpretationLine,
            stripPrefix,
            lineKind,
            normalizePlanetHeaderLine,
            isDecorativeOnlyLine,
            kindMeta,
            buildCompactPlanetEditorial,
            compactPlacementDegree,
            buildExpandedPlanetEditorial,
          }}
        />

      </div>
    </div>
  );
}
